/**
 * Tesis uyum takvimi.
 *
 * 26 Agustos 2026 yonetmelik degisikligi basit konaklama tesislerine yeni
 * asgari nitelikler getirdi ve 1 Kasim 2026'da yururluge giriyor: sertifikali
 * ilk yardim personeli, havuzlu tesislerde cankurtaran, duzenli temizlik ve
 * hasereyle mucadele gibi.
 *
 * DURUSTLUK NOTU (docs/14): yonetmelik dijital kayit tutmayi ZORUNLU KILMIYOR.
 * Bu modulun degeri "kanun istiyor" degil, "denetimde hazir olmak ve suresi
 * gecen sertifikayi kacirmamak". Ekranlarda da boyle anlatilir; hicbir yerde
 * "yasal zorunluluk" demeyiz.
 *
 * Asagidaki liste konaklama tesislerinde YAYGIN OLARAK istenen belgelerin
 * hatirlaticisidir, tam ve baglayici bir mevzuat listesi degildir. Tesis
 * kendi belgesini `DIGER` ile ekleyebilir; hicbir kalem zorunlu tutulmaz.
 * Yenileme periyotlari da varsayilandir - gercek sure belgenin kendi
 * uzerinde yazar ve kullanici duzeltebilir.
 */

export const UYUM_TIPLERI = [
  'TURIZM_BELGESI',
  'ISYERI_RUHSATI',
  'ILK_YARDIM_SERTIFIKASI',
  'CANKURTARAN_SERTIFIKASI',
  'ILACLAMA',
  'YANGIN_TUPU',
  'YANGIN_RAPORU',
  'ASANSOR_MUAYENE',
  'LPG_MUAYENE',
  'ELEKTRIK_TOPRAKLAMA',
  'KAPASITE_RAPORU',
  'SIGORTA',
  'VERBIS',
  'DIGER',
] as const;

export type UyumTipi = (typeof UYUM_TIPLERI)[number];

export interface UyumTipiTanim {
  ad: string;
  /** Varsayilan yenileme periyodu (ay). null: sureye bagli degil. */
  periyotAy: number | null;
  /** Bir kisiye mi bagli? (sertifikalarda kimin oldugu sorulur) */
  kisiyeBagli: boolean;
  /** Kullaniciya ne oldugunu anlatan kisa cumle. */
  aciklama: string;
}

export const UYUM_TANIMLARI: Record<UyumTipi, UyumTipiTanim> = {
  TURIZM_BELGESI: {
    ad: 'Turizm belgesi',
    periyotAy: null,
    kisiyeBagli: false,
    aciklama: 'Basit konaklama veya turizm işletme belgesi.',
  },
  ISYERI_RUHSATI: {
    ad: 'İşyeri açma ve çalışma ruhsatı',
    periyotAy: null,
    kisiyeBagli: false,
    aciklama: 'Belediyeden alınan ruhsat.',
  },
  ILK_YARDIM_SERTIFIKASI: {
    ad: 'İlk yardım sertifikası',
    periyotAy: 36,
    kisiyeBagli: true,
    aciklama: 'Sertifikalı ilk yardım personeli. Her personel için ayrı kayıt açın.',
  },
  CANKURTARAN_SERTIFIKASI: {
    ad: 'Cankurtaran sertifikası',
    periyotAy: 36,
    kisiyeBagli: true,
    aciklama: 'Havuzlu tesislerde aranıyor. Her cankurtaran için ayrı kayıt açın.',
  },
  ILACLAMA: {
    ad: 'İlaçlama (haşere mücadelesi)',
    periyotAy: 3,
    kisiyeBagli: false,
    aciklama: 'Periyodik ilaçlama belgesi.',
  },
  YANGIN_TUPU: {
    ad: 'Yangın tüpü dolumu',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'Söndürme tüplerinin dolum/bakım tarihi.',
  },
  YANGIN_RAPORU: {
    ad: 'Yangın güvenlik raporu',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'İtfaiyeden alınan uygunluk raporu.',
  },
  ASANSOR_MUAYENE: {
    ad: 'Asansör periyodik kontrolü',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'Yetkili kuruluşun yıllık kontrol etiketi.',
  },
  LPG_MUAYENE: {
    ad: 'LPG / doğalgaz tesisat kontrolü',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'Tesisat uygunluk belgesi.',
  },
  ELEKTRIK_TOPRAKLAMA: {
    ad: 'Elektrik topraklama ölçümü',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'Yetkili kuruluşun ölçüm raporu.',
  },
  KAPASITE_RAPORU: {
    ad: 'Kapasite raporu',
    periyotAy: null,
    kisiyeBagli: false,
    aciklama: 'Oda ve yatak kapasitesini gösteren rapor.',
  },
  SIGORTA: {
    ad: 'Sigorta poliçesi',
    periyotAy: 12,
    kisiyeBagli: false,
    aciklama: 'DASK, işyeri veya sorumluluk poliçesi.',
  },
  VERBIS: {
    ad: 'VERBİS kaydı',
    periyotAy: null,
    kisiyeBagli: false,
    aciklama: 'KVKK veri sorumluları sicili kaydı.',
  },
  DIGER: {
    ad: 'Diğer',
    periyotAy: null,
    kisiyeBagli: false,
    aciklama: 'Listede olmayan belge.',
  },
};

export type UyumDurumu = 'GECERLI' | 'YAKLASIYOR' | 'GECTI' | 'TARIH_YOK';

/**
 * Bir kaydin durumu.
 *
 * Tarih yoksa GECTI demeyiz: kullanici belgeyi kaydetmis ama tarihini
 * girmemis olabilir ve "gecti" demek yanlis alarm olur. Ayri bir durum.
 */
export function uyumDurumu(
  bitisTarihi: string | null | undefined,
  hatirlatmaGun: number,
  bugun: string,
): UyumDurumu {
  if (!bitisTarihi) return 'TARIH_YOK';
  if (bitisTarihi < bugun) return 'GECTI';
  const esik = gunEkle(bugun, Math.max(0, hatirlatmaGun));
  return bitisTarihi <= esik ? 'YAKLASIYOR' : 'GECERLI';
}

/** Kalan gun sayisi; gecmisse negatif. Tarih yoksa null. */
export function kalanGun(bitisTarihi: string | null | undefined, bugun: string): number | null {
  if (!bitisTarihi) return null;
  const ms = Date.parse(`${bitisTarihi}T00:00:00Z`) - Date.parse(`${bugun}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * Takvimde GERCEKTEN var olan bir gun mu?
 *
 * `/^\d{4}-\d{2}-\d{2}$/` tek basina yetmiyor: 2026-02-30 veya 2026-13-01
 * gibi olmayan gunleri gecirir, sonra Date.parse NaN dondurur ve kalan gun
 * hesabi sessizce bozulur. Bu yuzden Date'e cevirip geri yazarak dogrularız.
 */
export function takvimGunuMu(tarih: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tarih)) return false;
  const t = new Date(`${tarih}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === tarih;
}

/** UTC gun ekleme - ay/yil tasmasini Date halleder. */
export function gunEkle(tarih: string, gun: number): string {
  const t = new Date(`${tarih}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + gun);
  return t.toISOString().slice(0, 10);
}

/** Periyodu olan tipler icin sonraki bitis tarihi onerisi. */
export function sonrakiBitis(tip: UyumTipi, baslangic: string): string | null {
  const periyot = UYUM_TANIMLARI[tip].periyotAy;
  if (!periyot) return null;
  const t = new Date(`${baslangic}T00:00:00Z`);
  const gun = t.getUTCDate();
  t.setUTCMonth(t.getUTCMonth() + periyot);
  // Ay sonu tasmasi: 31 Ocak + 1 ay = 3 Mart olmasin, 28/29 Subat olsun.
  if (t.getUTCDate() !== gun) t.setUTCDate(0);
  return t.toISOString().slice(0, 10);
}
